import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import { FiChevronDown } from 'react-icons/fi';
import { useSession } from '@/hooks/useSession';
import usePermission from '@/hooks/usePermission';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { confirm } from '@/lib/confirm';
import { PATHS } from '@/routes/paths';
import { CourseModulePlayfulCard } from '../../Playful/CourseModulePlayfulCard';

// Wrapper do card lúdico de módulo com o que é exclusivo de professor/admin:
// menu de ações (publicar/despublicar, editar, excluir — atalho pro mesmo
// comportamento da página de gerenciamento de conteúdo) e permissões.
// Sem progresso/bloqueio — isso é conceito de matrícula de aluno.
export function CourseAboutPlayfulModuleCard(props) {
  const { course, module, index, details, isLoadingDetails, onRequestDetails, onDeleted } = props;
  const navigate = useNavigate();
  const client = useClient();
  const { abilities } = usePlatformAbilities();
  const currentUser = useSession()?.session?.user;

  const [showWarning, setShowWarning] = useState(false);
  const [unpublishWarning, setUnpublishWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const moduleForm = useFormik({
    initialValues: { publish: module?.publish }
  });

  useEffect(() => {
    moduleForm.setFieldValue('publish', module?.publish);
  }, [module.publish]);

  const hasManagePermission = usePermission('Gerenciar conteúdo', 'Cursos');
  const hasCoursePermission = hasManagePermission && (!course?.['authored-by-user'] || (course?.['authored-by-user'] && currentUser.id === course?.['creator-id']) || currentUser?.profileNames.includes('Administrador'));
  const hasModulePermission = hasManagePermission && (!module?.['authored_by_user'] || (module?.['authored_by_user'] && currentUser.id === module?.['creator_id']) || currentUser?.profileNames.includes('Administrador'));

  const hasLessonInModuleToPublish = module?.learning_system_items_total_count === 0 && !moduleForm.values.publish;
  const isCourseNotPublished = !course?.published;

  const updateModulePublicity = async values => {
    setIsSubmitting(true);
    const { error } = await client.mutate(['learning_systems', module.id], { publish: !values.publish });
    moduleForm.setFieldValue('publish', !moduleForm.values.publish);
    if (error) {
      toast.error('Erro ao atualizar conteúdo');
    } else {
      toast.success(moduleForm.values.publish ? 'Despublicado com sucesso' : 'Publicado com sucesso!');
    }
    setIsSubmitting(false);
    setShowWarning(false);
    setUnpublishWarning(false);
  };

  const handlePublishToggle = () => {
    if ((hasLessonInModuleToPublish || isCourseNotPublished) && !moduleForm.values.publish) {
      setShowWarning(true);
    } else if (moduleForm.values.publish) {
      setUnpublishWarning(true);
    } else {
      updateModulePublicity(moduleForm.values);
    }
  };

  // Mesmo comportamento do dropdown de ações da página de gerenciamento de
  // conteúdo (course-manage-sidebar.jsx) — isso aqui é só um atalho pra ele.
  const deleteModule = async () => {
    const { error } = await client.delete(`learning_systems/${module.id}`);
    if (error) {
      toast.error('Erro ao deletar módulo');
    } else {
      toast.success('Módulo excluido com sucesso!');
      onDeleted?.();
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteModule'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteModule();
    }
  };

  const filesCount = abilities?.['cursos-materiais'] ? (module?.['learning_system_files_count'] ?? 0) : 0;
  const activitiesCount = module?.['learning_system_activities_count'] ?? 0;
  const bookletsCount = abilities?.['avaliacoes-apostilas'] ? (module?.['learning_system_question_books_count'] ?? 0) : 0;
  const lessonsCount = module?.learning_system_items_total_count ?? 0;

  return (
    <>
      <CourseModulePlayfulCard
        moduleId={module.id}
        title={module?.title}
        officialContent={module?.['official_content']}
        index={index}
        lessonsCount={lessonsCount}
        onClick={() => navigate(`/show-course/${module.id}/classroom`)}
        cta={{ label: 'Acessar aulas', disabled: lessonsCount === 0, onClick: () => navigate(`/show-course/${module.id}/classroom`) }}
        activityHref={activity => PATHS.COMPLEMENTARY_ACTIVITY.BY_COURSE(activity.id, course?.id)}
        actions={
          hasModulePermission && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t('button.options')}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-[var(--color-bg-lilac)] bg-white text-[var(--color-secondary)] transition-[border-color,transform] duration-100 hover:border-[var(--color-secondary)] active:scale-[0.97]"
                >
                  <FiChevronDown size={20} />
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                {hasCoursePermission && hasModulePermission && <DropdownMenuItem onSelect={handlePublishToggle}>{moduleForm.values.publish ? t('lms.unpublish') : t('lms.publish')}</DropdownMenuItem>}
                {hasModulePermission && (
                  <>
                    <DropdownMenuItem onSelect={() => navigate(`/lms/criar/modulo/nome/${module.id}/${course?.id}`)}>{t('button.edit')}</DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-danger"
                      onSelect={handleDelete}
                    >
                      {t('button.delete')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
        filesCount={filesCount}
        activitiesCount={activitiesCount}
        bookletsCount={bookletsCount}
        details={details}
        isLoadingDetails={isLoadingDetails}
        onRequestDetails={onRequestDetails}
      />

      <ModalWarning
        show={showWarning}
        onClose={() => setShowWarning(false)}
        title={`Você tem certeza que deseja publicar o módulo${module?.learning_system_items_total_count === 0 ? ` sem uma aula publicada?` : '?'}`}
        description="Ao publicar o módulo, o curso também será publicado."
        onSubmitFirstButton={() => updateModulePublicity(moduleForm.values)}
        onSubmitSecondButton={() => setShowWarning(false)}
        isSubmitting={isSubmitting}
      />

      <ModalWarning
        show={unpublishWarning}
        onClose={() => setUnpublishWarning(false)}
        title="Ao despublicar, este conteúdo ficará invisível para os alunos."
        description="Você pode publicá-lo novamente a qualquer momento. Deseja prosseguir com a despublicação?"
        onSubmitFirstButton={() => updateModulePublicity(moduleForm.values)}
        onSubmitSecondButton={() => setUnpublishWarning(false)}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
